import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import { getLeadTracks } from "@/config/endpoints";
import { invalidateTrackQueries } from "./invalidateTrackQueries";

/** A live TRACK follow-up — separate from the lead's stage follow-ups. */
export interface LeadTrackFollowUp {
  id: number;
  date: string;
  note: string | null;
  isSystem: boolean;
  track: { id: number; name: string };
  role: { id: number; name: string } | null;
  holder: { id: number; name: string } | null;
  /** The deal, on a deal-based track's row (null/absent on a lead-based one). */
  child?: { id: number; name: string } | null;
}

export function useLeadTrackFollowUps(
  leadId: string | number | null | undefined
) {
  const queryClient = useQueryClient();
  const queryKey = ["track-followups", "lead", leadId];

  const query = useQuery({
    queryKey,
    enabled: Boolean(leadId),
    queryFn: async (): Promise<LeadTrackFollowUp[]> => {
      const { data } = await axiosInstance.get(
        `${getLeadTracks}/followups/lead/${leadId}`
      );
      return data?.data ?? [];
    },
  });

  const reschedule = useMutation({
    mutationFn: async (body: { id: number; date: string; note?: string }) => {
      const { id, ...rest } = body;
      const { data } = await axiosInstance.patch(
        `${getLeadTracks}/followups/${id}`,
        rest
      );
      return data;
    },
    onSuccess: () => {
      toast.success("Track follow-up updated");
      // Not just this drawer: the rail behind it must move the row to its new
      // bucket (Today → Later) and the Track members card must recount.
      invalidateTrackQueries(queryClient);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to update track follow-up"
      );
    },
  });

  return { followUps: query.data ?? [], reschedule };
}
